import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLoadCompanyDirectoryQuery } from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

// Tiny copy-to-clipboard button. Briefly swaps to a check icon on success.
// stopPropagation on the click so it doesn't bubble up to the card (which
// would open the modal).
function CopyButton({ value, title }) {
  const [copied, setCopied] = React.useState(false);
  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard not available — silently no-op so we don't surprise users */
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={title || 'Copy'}
      title={copied ? 'Copied!' : (title || 'Copy')}
      style={{
        background: 'transparent', border: 0, padding: '0 4px',
        color: copied ? '#1a7f4e' : '#0069a6',
        cursor: 'pointer', fontSize: '0.85rem', lineHeight: 1,
        verticalAlign: 'middle',
      }}
    >
      <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'}`} />
    </button>
  );
}
CopyButton.propTypes = {
  value: PropTypes.string.isRequired,
  title: PropTypes.string,
};

function initialsOf(first, last) {
  const a = (first || '').trim().charAt(0).toUpperCase();
  const b = (last  || '').trim().charAt(0).toUpperCase();
  return (a + b) || '?';
}

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatBirthday = (m, d) => (m && d ? `${MONTHS[m]} ${d}` : null);
const formatHireDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

function isBirthdayToday(emp) {
  if (!emp || !emp.dob_month || !emp.dob_day) return false;
  const now = new Date();
  return Number(emp.dob_month) === now.getMonth() + 1 && Number(emp.dob_day) === now.getDate();
}

function fireConfetti() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const colors = ['#0a2947', '#0069a6', '#d09b2c', '#ff6b9d', '#7d5fff', '#5fff7d', '#ff5f5f', '#ffd93d'];
  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  container.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2000;overflow:hidden;';
  document.body.appendChild(container);

  const COUNT = 110;
  const originX = window.innerWidth / 2;
  const originY = window.innerHeight * 0.3;
  const pieces = [];
  for (let i = 0; i < COUNT; i++) {
    const el = document.createElement('div');
    const color = colors[Math.floor(Math.random() * colors.length)];
    const size = 6 + Math.random() * 7;
    const isStrip = Math.random() < 0.65;
    el.style.cssText =
      `position:absolute;left:${originX}px;top:${originY}px;` +
      `width:${size.toFixed(1)}px;height:${(isStrip ? size * 0.4 : size).toFixed(1)}px;` +
      `background:${color};border-radius:${isStrip ? '1px' : '50%'};` +
      `will-change:transform,opacity;`;
    container.appendChild(el);
    const angle = (Math.random() - 0.5) * Math.PI + (-Math.PI / 2);
    const speed = 7 + Math.random() * 11;
    pieces.push({
      el,
      x: 0,
      y: 0,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      vr: (Math.random() - 0.5) * 18,
      rotation: Math.random() * 360,
    });
  }

  const start = performance.now();
  const DURATION = 3200;
  const FADE_MS = 700;
  function tick(now) {
    const elapsed = now - start;
    pieces.forEach(p => {
      p.vy += 0.35;
      p.vx *= 0.99;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.vr;
      p.el.style.transform = `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) rotate(${p.rotation.toFixed(0)}deg)`;
      if (elapsed > DURATION - FADE_MS) {
        p.el.style.opacity = String(Math.max(0, (DURATION - elapsed) / FADE_MS));
      }
    });
    if (elapsed < DURATION) {
      requestAnimationFrame(tick);
    } else {
      container.remove();
    }
  }
  requestAnimationFrame(tick);
}

Field.propTypes = { label: PropTypes.string, children: PropTypes.node, span: PropTypes.bool };
EmployeeCard.propTypes = { emp: PropTypes.object.isRequired, onClick: PropTypes.func };
EmployeeDetailModal.propTypes = { emp: PropTypes.object, onClose: PropTypes.func.isRequired };

function Field({ label, children, span }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div style={{ fontSize: '0.85rem', color: brand.charcoal, gridColumn: span ? '1 / -1' : 'auto' }}>
      <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, fontWeight: 700, lineHeight: 1, marginBottom: 2 }}>{label}</div>
      <div style={{ color: brand.deepBlue, wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

export function EmployeeCard({ emp, onClick }) {
  // On phones the 170px photo column crushes the info pane (especially with
  // the 80px collapsed sidebar still in the viewport). Stack the photo on top
  // and let the info area take the full card width.
  const isMobile = useIsMobile();
  const handleActivate = onClick
    ? () => {
        if (isBirthdayToday(emp)) fireConfetti();
        onClick();
      }
    : undefined;
  return (
    <div
      onClick={handleActivate}
      onKeyDown={(e) => { if (handleActivate && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleActivate(); } }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{
        background: 'white',
        borderRadius: 14,
        boxShadow: '0 2px 12px rgba(10,41,71,0.08)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(10,41,71,0.16)'; } }}
      onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(10,41,71,0.08)'; } }}
    >
      {/* Photo column */}
      <div style={{
        flexShrink: 0,
        width: isMobile ? '100%' : 170,
        background: `linear-gradient(160deg, ${brand.deepBlue} 0%, ${brand.tealBlue} 100%)`,
        padding: isMobile ? '1rem' : '1.25rem 1rem',
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: isMobile ? 'flex-start' : 'center',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: isMobile ? '0.9rem' : '0.75rem',
        position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: brand.goldenrod }} />
        {emp.photo_url ? (
          <img
            src={emp.photo_url}
            alt={`${emp.first_name} ${emp.last_name}`}
            style={{
              width: isMobile ? 64 : 110,
              height: isMobile ? 64 : 110,
              borderRadius: '50%', objectFit: 'cover', border: `3px solid ${brand.goldenrod}`, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              flexShrink: 0,
            }}
          />
        ) : (
          <div style={{
            width: isMobile ? 64 : 110,
            height: isMobile ? 64 : 110,
            borderRadius: '50%', background: 'rgba(255,255,255,0.15)', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
            fontSize: isMobile ? '1.25rem' : '2rem',
            border: `3px solid ${brand.goldenrod}`,
            flexShrink: 0,
          }}>
            {initialsOf(emp.first_name, emp.last_name)}
          </div>
        )}
        {emp.firm && (
          <div style={{
            background: brand.goldenrod, color: 'white',
            fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.75px',
            padding: '3px 12px', borderRadius: 999, whiteSpace: 'nowrap',
          }}>
            {emp.firm}
          </div>
        )}
      </div>

      {/* Info column */}
      <div style={{ flex: 1, minWidth: 0, padding: isMobile ? '0.9rem 1rem' : '1.1rem 1.25rem', display: 'flex', flexDirection: 'column' }}>
        <div style={{ borderBottom: `1px solid ${brand.softGray}`, paddingBottom: '0.75rem', marginBottom: '0.85rem' }}>
          <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: isMobile ? '1.05rem' : '1.15rem', lineHeight: 1.2 }}>
            {emp.first_name} {emp.last_name}
          </div>
          {emp.title && (
            <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginTop: 4 }}>{emp.title}</div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.7rem 1rem' }}>
          <Field label="Location">{emp.location || null}</Field>
          <Field label="Hire Date">{formatHireDate(emp.hire_date)}</Field>
          <Field label="Birthday">{formatBirthday(emp.dob_month, emp.dob_day)}</Field>
          <Field label="Phone">
            {emp.phone ? (
              <a href={`tel:${emp.phone}`} onClick={(e) => e.stopPropagation()} style={{ color: brand.tealBlue, textDecoration: 'none' }}>{emp.phone}</a>
            ) : null}
          </Field>
          <Field label="Email" span>
            {emp.final_email ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <a href={`mailto:${emp.final_email}`} onClick={(e) => e.stopPropagation()} style={{ color: brand.tealBlue, textDecoration: 'none', wordBreak: 'break-all' }}>{emp.final_email}</a>
              </span>
            ) : null}
          </Field>
          <Field label="Former Email" span>{emp.prior_email || null}</Field>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div style={{ padding: '0.6rem 0', borderBottom: `1px solid ${brand.softGray}` }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, fontWeight: 700, marginBottom: 2 }}>{label}</div>
      <div style={{ color: brand.deepBlue, fontSize: '0.95rem', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}
DetailRow.propTypes = { label: PropTypes.string, children: PropTypes.node };

export function EmployeeDetailModal({ emp, onClose }) {
  const isMobile = useIsMobile();
  const [photoZoomed, setPhotoZoomed] = React.useState(false);
  // Reset the zoom whenever we switch to a different employee (or close).
  React.useEffect(() => { setPhotoZoomed(false); }, [emp]);
  if (!emp) return null;
  const avatarSize = isMobile ? 72 : 110;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050,
        padding: isMobile ? '0.5rem' : '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: 14, maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 12px 40px rgba(10,41,71,0.3)' }}
      >
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: isMobile ? '1rem 1rem 1rem' : '1.25rem 1.5rem', position: 'relative' }}>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ position: 'absolute', top: 12, right: 14, background: 'transparent', border: 'none', color: 'white', fontSize: '1.1rem', cursor: 'pointer' }}
          >
            <i className="bi bi-x-lg" />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.75rem' : '1rem', paddingRight: '1.5rem' }}>
            {emp.photo_url ? (
              <img
                src={emp.photo_url}
                alt={`${emp.first_name} ${emp.last_name}`}
                onClick={(e) => { e.stopPropagation(); setPhotoZoomed(true); }}
                title="Click to enlarge"
                style={{ width: avatarSize, height: avatarSize, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${brand.goldenrod}`, flexShrink: 0, cursor: 'zoom-in' }}
              />
            ) : (
              <div style={{
                width: avatarSize, height: avatarSize, borderRadius: '50%', background: brand.tealBlue, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
                fontSize: isMobile ? '1.35rem' : '2rem',
                border: `3px solid ${brand.goldenrod}`, flexShrink: 0,
              }}>
                {initialsOf(emp.first_name, emp.last_name)}
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <h4 style={{ color: 'white', fontWeight: 800, margin: 0, fontSize: isMobile ? '1.15rem' : '1.4rem', wordBreak: 'break-word' }}>
                {emp.first_name} {emp.last_name}
              </h4>
              {emp.title && (
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', marginTop: 4 }}>{emp.title}</div>
              )}
              {emp.firm && (
                <div style={{ color: brand.goldenrod, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginTop: 6 }}>{emp.firm}</div>
              )}
            </div>
          </div>
        </div>
        <div style={{ padding: isMobile ? '0.5rem 1rem 0.75rem' : '0.75rem 1.5rem 1rem' }}>
          <DetailRow label="Location">{emp.location || null}</DetailRow>
          <DetailRow label="Hire Date">{formatHireDate(emp.hire_date)}</DetailRow>
          <DetailRow label="Birthday">{formatBirthday(emp.dob_month, emp.dob_day)}</DetailRow>
          <DetailRow label="Phone">
            {emp.phone ? (
              <a href={`tel:${emp.phone}`} style={{ color: brand.tealBlue, textDecoration: 'none' }}>{emp.phone}</a>
            ) : null}
          </DetailRow>
          <DetailRow label="Email">
            {emp.final_email ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <a href={`mailto:${emp.final_email}`} style={{ color: brand.tealBlue, textDecoration: 'none' }}>{emp.final_email}</a>
                <CopyButton value={emp.final_email} title="Copy email" />
              </span>
            ) : null}
          </DetailRow>
          <DetailRow label="Former Email">{emp.prior_email || null}</DetailRow>
          <DetailRow label="Reports To">{emp.manager_name || null}</DetailRow>
        </div>
      </div>

      {/* Photo lightbox — clicking the avatar enlarges it. Sits above the modal
          and swallows clicks so dismissing the lightbox doesn't close the card. */}
      {photoZoomed && emp.photo_url && (
        <div
          onClick={(e) => { e.stopPropagation(); setPhotoZoomed(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
            padding: '1.5rem', cursor: 'zoom-out',
          }}
        >
          <img
            src={emp.photo_url}
            alt={`${emp.first_name} ${emp.last_name}`}
            style={{
              maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain',
              borderRadius: 12, border: `4px solid ${brand.goldenrod}`,
              boxShadow: '0 12px 48px rgba(0,0,0,0.5)',
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function EmployeeDirectory() {
  const isMobile = useIsMobile();
  const { data: rows = [], isLoading, isError } = useLoadCompanyDirectoryQuery();
  const [query, setQuery] = useState('');
  const [firmFilter, setFirmFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const firmOptions = useMemo(() => {
    const s = new Set();
    rows.forEach(r => { if (r.firm) s.add(r.firm); });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (firmFilter !== 'all' && (r.firm || '') !== firmFilter) return false;
      if (!q) return true;
      const hay = [
        r.first_name, r.last_name, r.final_email, r.prior_email,
        r.title, r.firm, r.location, r.phone, r.prior_company,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, firmFilter]);

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>

      {/* Header banner */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: isMobile ? '1.25rem' : '1.75rem 2rem', marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-people-fill" style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Company Resources
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.25rem' : undefined }}>Employee Directory</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: '0.875rem' }}>
              Find your colleagues across Prosperity Capital Advisors.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(10,41,71,0.06)', padding: '0.75rem 1rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <i className="bi bi-search" style={{ color: brand.tealBlue }} />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Name, title, firm, location, email…"
          style={{
            flex: isMobile ? '1 1 100%' : 1,
            minWidth: isMobile ? 0 : 220,
            border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '6px 10px',
            fontSize: '0.875rem', color: brand.deepBlue, outline: 'none', background: 'white',
          }}
        />
        <select
          value={firmFilter}
          onChange={e => setFirmFilter(e.target.value)}
          style={{
            flex: isMobile ? '1 1 auto' : '0 1 auto',
            border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '6px 10px',
            fontSize: '0.875rem', color: brand.deepBlue, outline: 'none', background: 'white',
            maxWidth: isMobile ? '100%' : 180,
          }}
        >
          <option value="all">All firms</option>
          {firmOptions.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>
          {filtered.length} {filtered.length === 1 ? 'employee' : 'employees'}
        </span>
      </div>

      {isLoading ? (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <div className="spinner-border" style={{ color: brand.tealBlue }} />
        </div>
      ) : isError ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
          <i className="bi bi-exclamation-triangle" style={{ fontSize: '1.5rem', color: brand.goldenrod, display: 'block', marginBottom: '0.5rem' }} />
          Unable to load the directory. Please try again later.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
          <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
          {query ? `No employees match "${query}".` : 'No employees in the directory yet.'}
        </div>
      ) : (
        <div className="row g-3">
          {filtered.map(emp => (
            <div key={emp.id} className="col-12 col-md-6">
              <EmployeeCard emp={emp} onClick={() => setSelected(emp)} />
            </div>
          ))}
        </div>
      )}

      <EmployeeDetailModal emp={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
